<template>
  <v-container fluid fill-width class="ma-0 pa-2">
    <v-app-bar app class="primary">
      <v-icon color="white" large>mdi-account-supervisor</v-icon>
      <v-toolbar-title class="white--text pl-2"
        ><span>POD-Vis Administrator Console</span>
        <div class="subtitle-1">
          <span>Manage users, groups, and uploaded dataset permissions.</span>
        </div>
      </v-toolbar-title>
    </v-app-bar>

    <!-- Sessions -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2"
                ><span>Active Sessions</span>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container fluid fill-width>
        <v-row>
          <v-col cols="12">
            <v-data-table
              v-model="selected_sessions"
              :headers="getSessionTableHeaders()"
              :items="sessions"
              item-key="id"
              dense
              class="pt-2"
            >
              <template v-slot:item.issue_date="{ item }">
                <td class="subtitle-1 pa-0 ma-0 pl-4">
                  {{ formatDateDistance(item.issue_date) }}
                </td>
              </template>
              <template v-slot:item.exp_date="{ item }">
                <td class="subtitle-1 pa-0 ma-0 pl-4">
                  {{ formatDateDistance(item.exp_date) }}
                </td>
              </template>
              <template v-slot:item.last_access="{ item }">
                <td class="subtitle-1 pa-0 ma-0 pl-4">
                  {{ formatDateDistance(item.last_access) }}
                </td>
              </template>
              <template v-slot:item.actions="{ item }">
                <td class="subtitle-1 text-xs-right pa-0 ma-0">
                  <!--delete -->
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }"> <!-- TODO --> </span>
                    </template>
                    <span class="subtitle-1">Delete</span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12"> <!-- TODO --> </v-col>
        </v-row>
      </v-container>
    </v-sheet>

    <!-- Users -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2"
                ><span>Users</span>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container fluid fill-width>
        <v-row>
          <v-col cols="12">
            <v-data-table
              v-model="selected_users"
              :headers="getUserTableHeaders()"
              :items="users"
              item-key="id"
              show-select
              dense
              class="pt-2"
            >
              <template v-slot:item.actions="{ item }">
                <td class="subtitle-1 pa-0 ma-0 pl-4">
                  <!--delete -->
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <delete-user-icon-dialog
                          :user="item"
                          @userDeleted="userDeleted"
                        ></delete-user-icon-dialog>
                      </span>
                    </template>
                    <span class="subtitle-1">Delete</span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:item.roles="{ item }">
                <td class="pa-0 ma-0 pl-4">
                  <v-tooltip
                    v-for="role in getUserRoles(item)"
                    :key="'tt-' + item.id + '-' + role.name"
                    top
                    color="primary"
                    class="pl-1"
                  >
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon v-on="{ ...tooltip }">{{ role.icon }}</v-icon>
                    </template>
                    <span class="subtitle-1">{{ role.name }}</span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:item.groups="{ item }">
                <td class="pa-0 ma-0" style="text-align: left;">
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip label class="primary ml-4">{{
                          getUserGroups(item.id).length
                        }}</v-chip>
                      </span>
                    </template>
                    <span class="subtitle-1">
                      {{ item.name }} groups:<br clear="both" />
                      <ul>
                        <li
                          v-for="(g, gi) in getUserGroups(item.id)"
                          :key="'ug-' + gi"
                          class="ma-0 pa-0"
                        >
                          {{ g.group.name
                          }}<v-icon
                            v-if="g.is_leader"
                            color="white"
                            class="pl-1"
                            >mdi-human-male-board</v-icon
                          ><v-icon
                            v-if="g.is_contributor"
                            color="white"
                            class="pl-1"
                            >mdi-share-variant</v-icon
                          ><br clear="both" />
                        </li>
                      </ul>
                    </span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td></td>
                <td :colspan="headers.length - 1">
                  <v-list class="my-2">
                    <v-list-item
                      v-for="(g, gi) in getUserGroups(item.id)"
                      :key="'eug-' + gi"
                      class="ma-0 pa-0"
                      dense
                    >
                      <v-list-item-content class="pa-0 ma-0 pl-4">
                        {{ g.name }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </td>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4">
            <create-user-btn-dialog
              class="ml-2"
              @userCreated="userCreated"
            ></create-user-btn-dialog>
            <create-group-btn-dialog
              class="ml-2"
              :selected-users="selected_users"
              @groupCreated="groupCreated"
            ></create-group-btn-dialog>
          </v-col>
          <v-col cols="8">
            <div class="role-legend-wrap role-legend-wrap--users">
              <div class="role-legend">
                <div class="role-legend-group">
                  <span class="font-weight-bold">User Roles:</span>
                  <div class="role-legend-items">
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-account-supervisor</v-icon
                      >Administrator</span
                    >
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-content-copy</v-icon>Data
                      Copier</span
                    >
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-database-export</v-icon>Data
                      Exporter</span
                    >
                  </div>
                </div>
                <div class="role-legend-group">
                  <span class="font-weight-bold">Group Roles:</span>
                  <div class="role-legend-items">
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-human-male-board</v-icon
                      >Leader</span
                    >
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-share-variant</v-icon
                      >Contributor</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>

    <!-- Groups -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2"
                ><span>Groups</span>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container fluid fill-width>
        <v-row>
          <v-col cols="12">
            <v-data-table
              v-model="selected_groups"
              :headers="getGroupTableHeaders()"
              :items="groups"
              item-key="id"
              dense
              class="pt-2"
            >
              <template v-slot:item.actions="{ item }">
                <td class="subtitle-1 pa-0 ma-0 pl-4">
                  <!--delete -->
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <delete-group-icon-dialog
                          :group="item"
                          @groupDeleted="groupDeleted"
                        ></delete-group-icon-dialog>
                      </span>
                    </template>
                    <span class="subtitle-1">Delete</span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:item.users="{ item }">
                <td class="pa-0 ma-0" style="text-align: left;">
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip label class="primary ml-4">{{
                          item.users.length
                        }}</v-chip>
                      </span>
                    </template>
                    <span class="subtitle-1">
                      {{ item.name }} users:<br clear="both" />
                      <ul>
                        <li
                          v-for="(u, ui) in item.users"
                          :key="'uli-' + ui"
                          class="ma-0 pa-0"
                        >
                          {{ u.name
                          }}<v-icon
                            v-if="u.is_leader"
                            color="white"
                            class="pl-1"
                            >mdi-human-male-board</v-icon
                          ><v-icon
                            v-if="u.is_contributor"
                            color="white"
                            class="pl-1"
                            >mdi-share-variant</v-icon
                          ><br clear="both" />
                        </li>
                      </ul>
                    </span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <!-- TODO: add buttons for batch operations -->
          </v-col>
          <v-col cols="6">
            <div class="role-legend-wrap role-legend-wrap--groups">
              <div class="role-legend">
                <div class="role-legend-group">
                  <span class="font-weight-bold">Group Roles:</span>
                  <div class="role-legend-items">
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-human-male-board</v-icon
                      >Leader</span
                    >
                    <span class="role-legend-item"
                      ><v-icon class="pr-1">mdi-share-variant</v-icon
                      >Contributor</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>

    <!-- Projects -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2"
                ><span>Projects</span>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container fluid fill-width>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="getProjectTableHeaders()"
              :items="projects"
              item-key="id"
              class="pt-2"
              :loading="loadingProjects"
              dense
            >
              <template v-slot:item.project_name="{ item }">
                <td class="text-xs-center">
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        {{ item.project_name }}
                      </span>
                    </template>
                    <span class="subtitle-1"> {{ item.description }} </span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:item.user_id="{ item }">
                <td class="text-xs-center">
                  {{
                    item.user_id in id2user
                      ? id2user[item.user_id]['name']
                      : item.user_id
                  }}
                </td>
              </template>

              <template v-slot:item.group_id="{ item }">
                <td class="text-xs-center">
                  {{
                    item.group_id in id2group
                      ? id2group[item.group_id]['name']
                      : item.group_id
                  }}
                </td>
              </template>

              <template v-slot:item.studies="{ item }">
                <td class="pa-0 ma-0" style="text-align: left;">
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip label class="primary ml-4">{{
                          item.studies.length
                        }}</v-chip>
                      </span>
                    </template>
                    <span class="subtitle-1">
                      {{ item.project_name }} datasets [num_subjects]:<br
                        clear="both"
                      />
                      <ul>
                        <li
                          v-for="(s, si) in item.studies"
                          :key="'st-' + si"
                          class="ma-0 pa-0"
                        >
                          {{ s.study_name }} [{{ s.num_subjects }}]<br
                            clear="both"
                          />
                        </li>
                      </ul>
                    </span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:item.actions="{ item }">
                <td class="text-xs-right pa-0 ma-0"><!--delete --></td>
              </template>

              <template v-slot:item.is_public="{ item }">
                <td class="py-1" style="text-align: left;">
                  <v-tooltip v-if="item.is_public" top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-2 mr-2"
                          color="green lighten-4"
                        >
                          <v-icon center>mdi-lock-open-variant-outline</v-icon>
                        </v-chip>
                      </span>
                    </template>
                    <span class="subtitle-1"> Public </span>
                  </v-tooltip>

                  <v-tooltip v-else top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-2 mr-2"
                          color="red lighten-4"
                        >
                          <v-icon center>mdi-lock-outline</v-icon>
                        </v-chip>
                      </span>
                    </template>
                    <span class="subtitle-1"> Private </span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>
  </v-container>
</template>

<script>
import axios from 'axios';

import { mapState, mapActions } from 'vuex';
import { actions as notifyActions } from '@/store/modules/notifications/types';
import { state as authState } from '@/store/modules/auth/types';
import { formatDistance } from 'date-fns';
import CreateUserBtnDialog from '@/components/admin/CreateUserBtnDialog.vue';
import CreateGroupBtnDialog from '@/components/admin/CreateGroupBtnDialog.vue';
import DeleteGroupIconDialog from '@/components/admin/DeleteGroupIconDialog.vue';
import DeleteUserIconDialog from '@/components/admin/DeleteUserIconDialog.vue';

export default {
  name: 'Admin',
  components: {
    CreateUserBtnDialog,
    CreateGroupBtnDialog,
    DeleteGroupIconDialog,
    DeleteUserIconDialog,
  },
  data() {
    return {
      loadingSessions: false,
      sessions: [],
      selected_sessions: [],
      loadingUsers: false,
      users: [],
      selected_users: [],
      id2user: {},
      loadingGroups: false,
      groups: [],
      selected_groups: [],
      id2group: {},
      loadingProjects: false,
      projects: [],
      loadingDatasets: false,
      datasets: [],
      uid2groups: {},
    };
  },
  computed: {
    ...mapState('auth', {
      user: authState.USER,
    }),
  },
  watch: {
    users(new_users) {
      let uh = {};
      new_users.forEach(u => {
        uh[u.id] = u;
      });
      this.id2user = uh;
    },
    groups(new_groups) {
      // group id to group
      let gh = {};
      // user id to groups
      let u2g = {};
      new_groups.forEach(g => {
        gh[g.id] = g;
        g.users.forEach(u => {
          if (!(u.id in u2g)) {
            u2g[u.id] = [];
          }
          u2g[u.id].push({
            is_leader: u.is_leader,
            is_contributor: u.is_contributor,
            group: g,
          });
        });
      });
      this.id2group = gh;
      this.uid2groups = u2g;
    },
  },
  created() {},
  mounted() {
    this.loadSessions();
    this.loadUsers();
    this.loadGroups();
    this.loadProjects();
  },
  methods: {
    ...mapActions('notifications', {
      notifyError: notifyActions.NOTIFY_ERROR,
      notifySuccess: notifyActions.NOTIFY_SUCCESS,
    }),
    formatDateDistance(d) {
      return formatDistance(new Date(d), new Date(), { addSuffix: true });
    },
    loadSessions() {
      if (this.loadingSessions) return;
      this.loadingSessions = true;
      axios.get('/api/sessions').then(({ data }) => {
        this.sessions = data.sessions;
        this.loadingSessions = false;
      });
    },
    loadUsers() {
      if (this.loadingUsers) return;
      this.loadingUsers = true;
      axios.get('/api/users?include=users').then(({ data }) => {
        this.users = data.users;
        this.loadingUsers = false;
      });
    },
    loadGroups() {
      if (this.loadingGroups) return;
      this.loadingGroups = true;
      axios.get('/api/groups').then(({ data }) => {
        this.groups = data.groups;
        this.loadingGroups = false;
      });
    },
    loadProjects() {
      if (this.loadingProjects) return;
      this.loadingProjects = true;
      axios
        .get('/api/projects?include=studies&include=num_subjects')
        .then(({ data }) => {
          this.projects = data.projects;
          this.loadingProjects = false;
        });
    },
    loadDatasets() {
      if (this.loadingDatasets) return;
      this.loadingDatasets = true;
      axios.get('/api/studies').then(({ data }) => {
        this.datasets = data.datasets;
        this.loadingDatasets = false;
      });
    },
    userDeleted() {
      this.loadUsers();
    },
    userCreated() {
      this.loadUsers();
    },
    groupCreated() {
      this.loadGroups();
      this.selected_users = [];
      this.notifySuccess('Group created');
    },
    groupDeleted() {
      this.loadGroups();
    },
    getUserGroups(user_id) {
      if (user_id in this.uid2groups) {
        return this.uid2groups[user_id];
      }
      return [];
    },
    getUserRoles(user) {
      let groups = this.getUserGroups(user.id);
      let roles = [];
      groups.forEach(g => {
        if (g.group.name == 'Data Exporters') {
          roles.push({ name: 'Data Exporter', icon: 'mdi-database-export' });
        } else if (g.group.name == 'Data Copiers') {
          roles.push({ name: 'Data Copier', icon: 'mdi-content-copy' });
        } else if (g.group.name == 'Administrators') {
          roles.push({ name: 'Administrator', icon: 'mdi-account-supervisor' });
        }
      });
      roles.sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
      return roles;
    },
    getSessionTableHeaders() {
      let headers = [
        {
          text: 'ID',
          value: 'id',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'User',
          value: 'user.name',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Login time',
          value: 'issue_date',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Expiration time',
          value: 'exp_date',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Last activity',
          value: 'last_access',
          class: 'text-subtitle-1 font-weight-bold',
        },
      ];
      return headers;
    },
    getUserTableHeaders() {
      let headers = [
        {
          text: 'ID',
          value: 'id',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Name',
          value: 'name',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Email',
          value: 'email',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Institution',
          value: 'institution',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'User Roles',
          value: 'roles',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Groups',
          value: 'groups',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Edit/Delete',
          value: 'actions',
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold',
        },
        //        {
        //          text: '',
        //          value: 'data-table-expand',
        //        },
      ];
      return headers;
    },
    getGroupTableHeaders() {
      let headers = [
        {
          text: 'ID',
          value: 'id',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Name',
          value: 'name',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Description',
          value: 'description',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Users',
          value: 'users',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Edit/Delete',
          value: 'actions',
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold',
        },
      ];
      return headers;
    },
    getProjectTableHeaders() {
      let headers = [
        {
          text: 'ID',
          value: 'id',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Name',
          value: 'project_name',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Owner',
          value: 'user_id',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Group',
          value: 'group_id',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Primary Disease',
          value: 'primary_disease',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Datasets',
          value: 'studies',
          class: 'text-subtitle-1 font-weight-bold align-center',
        },
        //        {
        //          text: 'Description',
        //          value: 'description',
        //          class: 'text-subtitle-1 font-weight-bold',
        //        },
        {
          text: 'Access',
          value: 'is_public',
          class: 'text-subtitle-1 font-weight-bold text-xs-center',
        },
        //        {
        //          text: 'Edit/Delete',
        //          value: 'actions',
        //          sortable: false,
        //          class: 'text-subtitle-1 font-weight-bold',
        //        },
      ];
      return headers;
    },
  },
};
</script>

<style scoped>
/* Role legends adapt to the space available in their own column (via container
   queries, so each legend responds to its column width rather than the viewport):

   State 1 (room for everything): groups side by side, items inline in a row.
     User Roles: a, b, c    Group Roles: d, e
   State 2 (medium): groups still side by side, but each group's items stack
     into a column beneath its header.
     User Roles: a      Group Roles: d
                 b                  e
                 c
   State 3 (tight): the groups also stack vertically.
     User Roles: a
                 b
                 c
     Group Roles: d
                  e

   Each legend gets its own named container so its thresholds suit its own
   content length (the Users legend is much longer than the Groups legend).
   The max-width thresholds below are tunable if the transitions feel early/late. */
.role-legend-wrap--users {
  container: roleusers / inline-size;
}
.role-legend-wrap--groups {
  container: rolegroups / inline-size;
}
.role-legend {
  display: flex;
  justify-content: flex-end; /* state 1: right-aligned, as before */
  column-gap: 24px;
  row-gap: 8px;
}
.role-legend-group {
  display: flex;
  align-items: baseline; /* header sits beside its first item */
  column-gap: 8px;
}
.role-legend-group > span {
  white-space: nowrap; /* never split "User Roles" / "Group Roles" across lines */
}
.role-legend-items {
  display: flex;
  column-gap: 8px; /* state 1: items in a row */
}
.role-legend-item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* Users legend (longer): state 2 stacks items within each group, state 3 stacks
   the groups too. */
@container roleusers (max-width: 800px) {
  .role-legend {
    justify-content: flex-start;
  }
  .role-legend-items {
    flex-direction: column;
    row-gap: 2px;
  }
}
@container roleusers (max-width: 480px) {
  .role-legend {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Groups legend (short, single group): only needs to stack its items, and only
   once the column is genuinely narrow. */
@container rolegroups (max-width: 320px) {
  .role-legend {
    justify-content: flex-start;
  }
  .role-legend-items {
    flex-direction: column;
    row-gap: 2px;
  }
}
</style>
